/**
 * One operation for "the project now has structures": create / fill the proposal
 * structures from the timber lists (step 1) and split every other whole-project
 * track into structure groups (step 2).
 *
 * Step 2 is drafted before the step-1 structures exist, so groups are built from
 * *slots*: a slot is either a wizard row (→ the structure(s) it will create — one,
 * or several when a CSV is split by Building) or a structure already on the
 * proposal. At commit, slots resolve to real `StructureRef`s and every included
 * track forks once per group via `forkStreamIntoStructures`.
 */

import type { ProjectFile, ProjectFileStream } from '../types';
import type { StructureRef } from './proposalStructureRefs';
import { structureSelectionLabel } from './structureSelectionLabel';
import {
  forkStreamIntoStructures,
  recordStreamStructureSetup,
  updateProjectFileStreamStructures,
  type ForkStreamTarget,
} from './projectFileService';
import {
  createProposalStructuresFromTimberLists,
  isLiveStructureRow,
  structureNamesForRow,
  type StructureFromTimberListResult,
  type StructureFromTimberListRow,
} from './proposalStructuresFromTimberLists';

export const ROW_SLOT_PREFIX = 'row:';
export const STRUCTURE_SLOT_PREFIX = 'struct:';

/** Something a step-2 group can contain. Shaped like a `StructureRef` so the structure pickers can list it. */
export interface StructureSlot {
  id: string;
  name: string;
  /** Wizard row this slot stands for (its structures do not exist yet). */
  rowKey?: string;
  /** Existing proposal structure. */
  structureId?: string;
  /** Names this slot will resolve to (several when a CSV is split by group). */
  structureNames: string[];
}

export interface StructureGroupDraft {
  key: string;
  slotIds: string[];
  /** Blank = auto label from the structure names. */
  label: string;
  labelEdited: boolean;
}

export const rowSlotId = (rowKey: string) => `${ROW_SLOT_PREFIX}${rowKey}`;
export const structureSlotId = (structureId: string) => `${STRUCTURE_SLOT_PREFIX}${structureId}`;

/**
 * Slots for step 2: one per live wizard row (in row order), then every proposal
 * structure no row refers to.
 */
export function structureSlotsForSetup(
  rows: ReadonlyArray<StructureFromTimberListRow>,
  structures: ReadonlyArray<StructureRef>,
): StructureSlot[] {
  const slots: StructureSlot[] = [];
  const usedExisting = new Set<string>();
  for (const row of rows) {
    if (!isLiveStructureRow(row)) continue;
    if (row.existingStructureId) {
      const st = structures.find(s => s.id === row.existingStructureId);
      if (!st) continue;
      usedExisting.add(st.id);
      slots.push({ id: rowSlotId(row.key), name: st.name, rowKey: row.key, structureId: st.id, structureNames: [st.name] });
      continue;
    }
    const names = structureNamesForRow(row);
    if (!names.length) continue;
    slots.push({
      id: rowSlotId(row.key),
      name: structureSelectionLabel(names, { maxNamed: 3 }),
      rowKey: row.key,
      structureNames: names,
    });
  }
  for (const st of structures) {
    if (usedExisting.has(st.id)) continue;
    slots.push({ id: structureSlotId(st.id), name: st.name, structureId: st.id, structureNames: [st.name] });
  }
  return slots;
}

let groupSeq = 0;
const nextGroupKey = () => `sg${++groupSeq}`;

/** Default groups: one per slot (a CSV split by Building is already one group). */
export function defaultStructureGroups(slots: ReadonlyArray<StructureSlot>): StructureGroupDraft[] {
  return slots.map(s => ({ key: nextGroupKey(), slotIds: [s.id], label: '', labelEdited: false }));
}

export function emptyStructureGroup(): StructureGroupDraft {
  return { key: nextGroupKey(), slotIds: [], label: '', labelEdited: false };
}

/** Names a group resolves to (flattening multi-structure slots). */
export function groupStructureNames(group: Pick<StructureGroupDraft, 'slotIds'>, slots: ReadonlyArray<StructureSlot>): string[] {
  const out: string[] = [];
  for (const id of group.slotIds) {
    const slot = slots.find(s => s.id === id);
    if (slot) out.push(...slot.structureNames);
  }
  return out;
}

export function groupLabel(group: StructureGroupDraft, slots: ReadonlyArray<StructureSlot>): string {
  if (group.labelEdited && group.label.trim()) return group.label.trim();
  return structureSelectionLabel(groupStructureNames(group, slots), { maxNamed: 3 });
}

/**
 * Whole-project tracks with versions that could continue under the structure groups:
 * untagged, not already a split root / fork, and not a timber-list track a wizard row
 * is about to tag.
 */
export function splitCandidateTracks(
  streams: ReadonlyArray<ProjectFileStream>,
  rows: ReadonlyArray<StructureFromTimberListRow> = [],
): ProjectFileStream[] {
  const claimed = new Set(rows.filter(isLiveStructureRow).map(r => r.timberListStreamId).filter(Boolean));
  return streams.filter(
    s =>
      !!s.id &&
      !claimed.has(s.id) &&
      (s.structureIds ?? []).filter(Boolean).length === 0 &&
      !s.wholeProjectRoot &&
      !s.forkedFromStreamId &&
      ((s.lastVersionIndex ?? 0) > 0 || !!s.headFileId),
  );
}

/** Which drafted groups a whole-project track continues under; `'all'` = every group. */
export type TrackGroupSelection = string[] | 'all';

export interface ProjectStructureSetupPlan {
  rows: StructureFromTimberListRow[];
  /** Step 2; empty = no split. */
  groups: StructureGroupDraft[];
  /**
   * Track id → the groups it continues under. One group: the track is simply tagged to
   * it (keeps its history, no root/fork). Two or more: it forks into exactly those.
   * Tracks absent here (or with an empty list) stay Whole project.
   */
  trackGroups: Record<string, TrackGroupSelection>;
}

export interface ProjectStructureSetupResult {
  /** `null` when no row was live (split only). */
  structures: StructureFromTimberListResult | null;
  forked: { streamId: string; forkedStreamIds: string[] }[];
  /** Tracks tagged straight to a single group (no fork). */
  tagged: { streamId: string; structures: StructureRef[] }[];
  /** A good track to select afterwards (first fork, else first tagged track). */
  focusStreamId: string | null;
  failures: string[];
}

/** Groups a track continues under, resolved against the live groups. Exported for the wizard. */
export function groupsForTrack(
  selection: TrackGroupSelection | undefined,
  liveGroups: ReadonlyArray<StructureGroupDraft>,
): StructureGroupDraft[] {
  if (!selection) return [];
  if (selection === 'all') return [...liveGroups];
  const wanted = new Set(selection);
  return liveGroups.filter(g => wanted.has(g.key));
}

/** How a track will be handled for a selection: nothing, tagged to one group, or forked into several. */
export function trackOutcome(
  selection: TrackGroupSelection | undefined,
  liveGroups: ReadonlyArray<StructureGroupDraft>,
): { kind: 'whole' } | { kind: 'tag'; group: StructureGroupDraft } | { kind: 'fork'; groups: StructureGroupDraft[] } {
  const groups = groupsForTrack(selection, liveGroups);
  if (groups.length === 0) return { kind: 'whole' };
  if (groups.length === 1) return { kind: 'tag', group: groups[0] };
  return { kind: 'fork', groups };
}

export interface SetUpProjectStructuresInput {
  dealId: string;
  projectId: string;
  plan: ProjectStructureSetupPlan;
  streams: ReadonlyArray<ProjectFileStream>;
  files: ReadonlyArray<ProjectFile>;
  /** Structures on the proposal before this run. */
  structures: ReadonlyArray<StructureRef>;
  actor: { uid: string; name: string };
  fetchText?: (url: string) => Promise<string>;
  /** Injectable for tests. */
  createStructures?: typeof createProposalStructuresFromTimberLists;
  fork?: typeof forkStreamIntoStructures;
  tagTrack?: typeof updateProjectFileStreamStructures;
  stampTrack?: typeof recordStreamStructureSetup;
}

/** Resolve the drafted groups to fork targets for one track. Exported for tests. */
export function forkTargetsForTrack(
  track: Pick<ProjectFileStream, 'title'>,
  groups: ReadonlyArray<StructureGroupDraft>,
  slots: ReadonlyArray<StructureSlot>,
  resolveSlot: (slot: StructureSlot) => StructureRef[],
): ForkStreamTarget[] {
  const targets: ForkStreamTarget[] = [];
  for (const g of groups) {
    const structures: StructureRef[] = [];
    for (const id of g.slotIds) {
      const slot = slots.find(s => s.id === id);
      if (slot) structures.push(...resolveSlot(slot));
    }
    if (!structures.length) continue;
    const label =
      g.labelEdited && g.label.trim() ? g.label.trim() : structureSelectionLabel(structures.map(s => s.name), { maxNamed: 3 });
    targets.push({ title: `${label} — ${track.title}`, structures });
  }
  return targets;
}

export async function setUpProjectStructures(input: SetUpProjectStructuresInput): Promise<ProjectStructureSetupResult> {
  const { dealId, projectId, plan, streams, files, structures, actor } = input;
  const createStructures = input.createStructures ?? createProposalStructuresFromTimberLists;
  const fork = input.fork ?? forkStreamIntoStructures;
  const tagTrack = input.tagTrack ?? updateProjectFileStreamStructures;
  const stampTrack = input.stampTrack ?? recordStreamStructureSetup;
  const result: ProjectStructureSetupResult = { structures: null, forked: [], tagged: [], focusStreamId: null, failures: [] };

  const liveRows = plan.rows.filter(isLiveStructureRow);
  if (liveRows.length) {
    result.structures = await createStructures({
      dealId,
      projectId,
      rows: liveRows,
      streams,
      files,
      actor,
      fetchText: input.fetchText,
    });
    result.failures.push(...result.structures.failures);
    result.focusStreamId = result.structures.taggedStreams[0]?.streamId ?? null;
  }

  const liveGroups = plan.groups.filter(g => g.slotIds.length > 0);
  const trackIds = Object.keys(plan.trackGroups ?? {}).filter(Boolean);
  if (!trackIds.length || !liveGroups.length) return result;

  const slots = structureSlotsForSetup(plan.rows, structures);
  const byRow = result.structures?.structuresByRow ?? {};
  const resolveSlot = (slot: StructureSlot): StructureRef[] => {
    if (slot.rowKey) return byRow[slot.rowKey] ?? [];
    const st = structures.find(s => s.id === slot.structureId);
    return st ? [st] : [];
  };

  let firstFork: string | null = null;
  let firstTagged: string | null = null;
  for (const streamId of trackIds) {
    const track = streams.find(s => s.id === streamId);
    if (!track) continue;
    const chosen = groupsForTrack(plan.trackGroups[streamId], liveGroups);
    const targets = forkTargetsForTrack(track, chosen, slots, resolveSlot);
    if (!targets.length) continue;
    if (targets.length === 1) {
      // One group: the track simply belongs to it — keep its history, no root / fork.
      try {
        await tagTrack(projectId, streamId, targets[0].structures);
        result.tagged.push({ streamId, structures: targets[0].structures });
        if (!firstTagged) firstTagged = streamId;
        try {
          await stampTrack(projectId, streamId, { byUid: actor.uid, byName: actor.name || undefined, createdStructure: false });
        } catch (err) {
          console.warn('[projectStructureSetup] could not stamp tagged track', err);
        }
      } catch (err) {
        result.failures.push(`${track.title}: ${err instanceof Error ? err.message : 'could not tag the track'}`);
      }
      continue;
    }
    try {
      const res = await fork(projectId, streamId, targets, actor);
      result.forked.push({ streamId, forkedStreamIds: res.forkedStreamIds });
      if (!firstFork && res.forkedStreamIds[0]) firstFork = res.forkedStreamIds[0];
    } catch (err) {
      result.failures.push(`${track.title}: ${err instanceof Error ? err.message : 'could not split the track'}`);
    }
  }
  if (firstFork) result.focusStreamId = firstFork;
  else if (firstTagged) result.focusStreamId = firstTagged;
  return result;
}
